// HUB-458 (epic HUB-440): the checkout request must carry the client's
// explicit "one discount per order" intent — see
// docs/review-discount-payments-integration.md §2.0/§2.3 (oppizi-api).
// A missing `discount_choice` is treated as `null` (full price) server-side,
// so silence here is never neutral for the "review" branch — it must only
// ever be sent when genuinely chosen.
//
// Pure so it's cheaply testable without mounting the full submit hook stack
// (FeatureFlagContext, react-query, apiClient, etc.).
export type CheckoutDiscountChoice = "review" | "coupon" | null;

export type DeriveCheckoutDiscountChoiceArgs = {
  /** is_review_discount_enabled (PostHog) */
  isReviewDiscountEnabled: boolean;
  /** OrderDetailsDrawer's useDiscountExclusivity.discountChoice, lifted to the submit flow */
  discountChoice: CheckoutDiscountChoice;
  /** campaignData.stripeCouponCode is set — an ACTUAL manual coupon is persisted (not just "chosen" mid-swap with nothing typed yet) */
  hasStripeCoupon: boolean;
};

/**
 * Returns `undefined` when the field must be OMITTED from the request body
 * entirely (zero-footprint rule — same principle as every other
 * review-discount surface: flag off means this feature doesn't exist for the
 * request, not "exists and says no"). `JSON.stringify` drops `undefined`
 * values, so the caller can pass this straight through without an extra
 * conditional.
 */
export const deriveCheckoutDiscountChoice = ({
  isReviewDiscountEnabled,
  discountChoice,
  hasStripeCoupon,
}: DeriveCheckoutDiscountChoiceArgs): CheckoutDiscountChoice | undefined => {
  if (!isReviewDiscountEnabled) return undefined;

  if (discountChoice === "review") return "review";

  // Keyed off the ACTUAL persisted coupon, not the `discountChoice` value —
  // `discountChoice` can be "coupon" mid-swap (reviewToCoupon confirmed,
  // coupon input just enabled, nothing entered yet) with no real coupon code
  // to send. Per the payments validation matrix, `"coupon"` with no code is
  // a 4xx (`missing_coupon_code`); sending `null` instead is always safe
  // (full price, no discount) and never rejected.
  if (hasStripeCoupon) return "coupon";

  return null;
};
